/** Compare to firebase:functions/src/models/user.ts */

import {doc} from 'firebase/firestore';
import {getCollectionRef, getFirestoreInstance} from '../refs';
// eslint-disable-next-line @typescript-eslint/no-unused-vars -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
import {pickBy} from 'lodash';

import type {
  DocumentData,
  DocumentReference,
  FirestoreDataConverter,
  QueryDocumentSnapshot,
  SnapshotOptions,
} from 'firebase/firestore';

import type {CountryCode} from '../../data/countries/country_code_to_names';
import type {CourseId, UserId} from '../../types/common';
import {SmallGroup} from './SmallGroup';
import {isFirebaseRole, RoleFirebaseString} from 'types/role';
import type {SectionDocument} from 'course/section/models/sectionDocument';
import { CertificateId } from 'course/certificate/certificateUtils';

// TODO: complete the User model schema
export type User = {
  id: string;
  roles: Record<CourseId, RoleFirebaseString>;
  sections: Record<CourseId, Array<DocumentReference<SectionDocument>>>;
  smallGroups: Record<CourseId, Array<DocumentReference<SmallGroup>>>;
  photoURL: string;
  displayName: string;
  firstName: string;
  lastName: string;
  email: string;
  isMinor: boolean;
  city: string;
  shareChatGPTConvos: boolean;
  shareCode: boolean;
  sharePeerLearn: boolean;
  country: CountryCode | '';
  portfolio: Array<string>;
  confirmedStudent: Record<CourseId, boolean>;
  confirmedSectionLeader: Record<CourseId, boolean>;
  /** Set when a user completes the review onboarding (senior SLs). */
  confirmedReviewer: Record<CourseId, boolean>;
  /**
   * Set when a senior SL completes the teaching onboarding but opts out of
   * leading a section this course. Record-only (no invariant depends on it);
   * distinguishes a teaching senior SL from a review-only one.
   */
  confirmedSectionOptOut: Record<CourseId, boolean>;
  confirmedMentor: Record<CourseId, boolean>;
  okToEmail: boolean;
  sentFirstEmail: boolean;
  aboutMe: string;
  goals: string;
  wantToConnect: boolean;
  okToSendNotifications: boolean;
  /**
   * Per-user opt-out for section-reminder emails (T-24h and T-1h
   * fired by the `@cip/functions` reminder cron). Defaults to `true`
   * when absent on the user doc. Outranked downstream by the global
   * `okToEmail` flag — a user with `okToEmail === false` is filtered
   * regardless of this setting.
   *
   * Coverage caveat: the reminder cron currently iterates only
   * `['cip6']`, so this field is effective only for that course
   * today. Tracked under umbrella
   * [#2587](https://github.com/CodeinPlace/cip/issues/2587) and child
   * [#2588](https://github.com/CodeinPlace/cip/issues/2588) — once the
   * cron iterates active courses this field will gate reminders for
   * all of them.
   */
  okToSendSectionReminders: boolean;
  finalProjectCommunities: Record<CourseId, Array<string>>;
  preferredLanguage: string;
};

const USER_CONVERTER: FirestoreDataConverter<User> = {
  toFirestore: (user: User): DocumentData => {
    return user;
  },

  fromFirestore(
    snapshot: QueryDocumentSnapshot<DocumentData>,
    options: SnapshotOptions,
  ): User {
    const data = snapshot.data(options);

    // `country` has historically been stored as an object on Firestore
    // (of type {country: string, eng_name: string, native_name: string})
    //
    // instead of storing derived data, we should transition to storing
    // only the country code and computing the derived data dynamically
    // so that it doesn't get out of sync if our data source changes. this
    // handles converting the multiple data types into a consistent interface
    let country: CountryCode | '';
    switch (typeof data.country) {
      case 'string':
        country = data.country as CountryCode;
        break;
      case 'undefined':
        country = '';
        break;
      default:
        country = data.country.code || '';
        break;
    }

    return {
      ...data,
      country,
      id: snapshot.id,
    } as User;
  },
};

const ROLE_CONVERTER: FirestoreDataConverter<RoleFirebaseString> = {
  toFirestore: (role: RoleFirebaseString): DocumentData => {
    return {
      role: role,
    };
  },

  fromFirestore(
    snapshot: QueryDocumentSnapshot<DocumentData>,
    options: SnapshotOptions,
  ): RoleFirebaseString {
    const data = snapshot.data(options);
    if (!data?.role || !isFirebaseRole(data.role)) {
      console.warn(
        `Found invalid role in Firestore: ${data?.role}. Treating as unregistered.`,
      );
      return 'unregistered';
    }
    return data.role;
  },
};

export const getUserCollectionRef = () =>
  getCollectionRef('users', USER_CONVERTER);
export const getUserDocumentRef = (id: UserId) =>
  doc(getUserCollectionRef(), id);

export const getUserRoleRef = (
  userId: UserId,
  courseId: CourseId,
): DocumentReference<RoleFirebaseString> => {
  if (!userId || !courseId) {
    console.error('User ID or course ID not set', {userId, courseId});
    return null;
  }
  return doc(
    getFirestoreInstance(),
    'users',
    userId,
    'roles',
    courseId,
  ).withConverter(ROLE_CONVERTER);
};

const CERTIFICATE_CONVERTER: FirestoreDataConverter<CertificateId> = {
  toFirestore: (certificateId: CertificateId): DocumentData => {
    return {
      certificateId: certificateId,
    };
  },

  fromFirestore(
    snapshot: QueryDocumentSnapshot<DocumentData>,
    options: SnapshotOptions,
  ): CertificateId {
    const data = snapshot.data(options);
    if (!data?.certificateId) {
      console.warn(
        `No valid certificateId found.`,
      );
      return null;
    }
    return data?.certificateId;
  },
};

export const getUserCertificateIDRef = (
  userId: UserId,
  courseId: CourseId,
): DocumentReference<CertificateId> => {
  if (!userId || !courseId) {
    console.error('User ID or course ID not set', {userId, courseId});
    return null;
  }
  return doc(
    getFirestoreInstance(),
    'users',
    userId,
    courseId,
    'certificate',
  ).withConverter(CERTIFICATE_CONVERTER);
};